import { useSendPasswordResetEmail } from "react-firebase-hooks/auth";
import { auth } from "../config/firebaseConfig";
import { Link } from "react-router-dom";
import UnAuthenticatedLayout from "../layouts/UnAuthenticatedLayout";
import { Zap, AlertCircle, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "react-hot-toast";
import {
  AuthCard,
  AuthCardContainer,
  AuthCardHeader,
  AuthCardLogo,
  AuthCardTitle,
} from "@/components/ui/auth-card";

const ForgotPasswordView = () => {
  const [sendPasswordResetEmail, sending, error] =
    useSendPasswordResetEmail(auth);
  const [email, setEmail] = useState("");

  const onFinish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    const url = import.meta.env.VITE_APP_URL;

    try {
      const res = await sendPasswordResetEmail(email, { url });

      if (res) {
        toast.success("Ett mail med instruktioner har skickats till din e-post!");
        setEmail("");
      } else {
        toast.error("Något gick fel, var vänlig försök igen!");
      }
    } catch (error) {
      toast.error("Något gick fel, var vänlig försök igen!");
    }
  };

  return (
    <UnAuthenticatedLayout>
      <AuthCard>
        <AuthCardHeader>
          <AuthCardLogo 
            src="https://graftus.ai/wp-content/themes/seodr/assets/img/logo.svg" 
            alt="Graftus AI" 
          />
          <AuthCardTitle>
            Återställ ditt lösenord
          </AuthCardTitle>
        </AuthCardHeader>

        <AuthCardContainer>
          {error && (
            <div className="mb-6 rounded-xl border border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.1)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-[#ff6b6b]" />
                <div className="text-[#ff6b6b] font-semibold">
                  Ett fel uppstod
                </div>
              </div>
              <div className="text-[rgba(255,107,107,0.8)] text-sm">
                {error.message}
              </div>
            </div>
          )}

          <form onSubmit={onFinish} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80 text-sm font-medium">
                E-post
              </Label>
              <div className="relative">
                <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#a8b8ff]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="namn@dittföretag.se"
                  autoComplete="username"
                  className="pl-10 bg-white/90 border-white/20 rounded-xl text-gray-900 h-12 text-base transition-all duration-300 hover:bg-white hover:border-[rgba(120,119,198,0.6)] hover:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] focus:bg-white focus:border-[rgba(120,119,198,0.6)] focus:shadow-[0_0_0_3px_rgba(120,119,198,0.1)] placeholder:text-gray-500"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:from-[#5a6fd8] hover:to-[#6a4190] border-none rounded-xl h-12 text-base font-semibold transition-all duration-300 hover:transform hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(102,126,234,0.3)] active:transform-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <Mail className="h-[18px] w-[18px]" />
              {sending ? "Skickar..." : "Återställ lösenord"}
            </Button>

            <div className="text-center">
              <Link 
                to="/sign-in" 
                className="text-white/70 text-sm transition-colors duration-200 hover:text-[#a8b8ff]"
              >
                Tillbaka till inloggning
              </Link>
            </div>
          </form>
        </AuthCardContainer>
      </AuthCard>
    </UnAuthenticatedLayout>
  );
};

export default ForgotPasswordView;
